/**
 * Загрузка блока тренажёра (T3, решение владельца 30.10.2026): «задания закончились» ученик не видит никогда.
 *
 * Цепочка: новое → повтор с ошибками → повтор пройденных раньше (самые давние) → только если нечего и там —
 * экран «Мы уже создаём новые задания» (`creating`). Ступень пропускается по 404; сервер шага 6 сам отдаёт
 * повтор на обычный запрос, прежний (30.09) понимает только `repeat=mistakes`.
 *
 * Неожиданный ответ (сеть, 5xx, 2xx без блока) — тихий повтор того же запроса с паузами; не вышло — `failed`
 * (карточка с «Повторить»), а не белый экран и не вечная загрузка. 409 — сначала мок; 403/423/429 — причина
 * (закрыто, вне расписания, предел) показывается как есть.
 */
import type { TrainerUnit, UnitRepeat } from '../api/ielts';

export type TrainerLoad =
  | { kind: 'unit'; unit: TrainerUnit; repeat?: UnitRepeat }
  | { kind: 'needsMock' }
  | { kind: 'gate'; error: unknown }
  | { kind: 'creating' }
  | { kind: 'failed' };

/** Паузы тихих повторов, мс. После последней — `failed`. */
export const RETRY_DELAYS = [1000, 3000, 7000];

const CHAIN: (UnitRepeat | undefined)[] = [undefined, 'mistakes', 'passed'];

/** Блок, который экран умеет показать: есть сессия и группы с вопросами или задание письма/речи. */
export function usableUnit(u: unknown): u is TrainerUnit {
  if (!u || typeof u !== 'object') return false;
  const unit = u as TrainerUnit;
  if (typeof unit.session_id !== 'string' || !unit.session_id) return false;
  if (unit.prompt && typeof unit.prompt === 'object') return true;
  return Array.isArray(unit.groups) && unit.groups.length > 0 && unit.groups.every((g) => Array.isArray(g?.questions));
}

/**
 * Пометка повтора: что прислал сервер; «с ошибками» прежний сервер понимает — тогда по запросу. «Пройденные»
 * прежний сервер не знает и на такой запрос отдаёт обычный блок — без поля пометку не ставим.
 */
function repeatOf(unit: TrainerUnit, asked: UnitRepeat | undefined): UnitRepeat | undefined {
  if (unit.repeat === 'mistakes' || unit.repeat === 'passed') return unit.repeat;
  return asked === 'mistakes' ? 'mistakes' : undefined;
}

export async function loadTrainerUnit(
  fetchUnit: (repeat?: UnitRepeat) => Promise<TrainerUnit>,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<TrainerLoad> {
  for (const asked of CHAIN) {
    for (let tries = 0; ; tries++) {
      let status: number | undefined;
      try {
        const unit = await fetchUnit(asked);
        if (usableUnit(unit)) return { kind: 'unit', unit, repeat: repeatOf(unit, asked) };
      } catch (e) {
        status = (e as { status?: number } | undefined)?.status;
        if (status === 409) return { kind: 'needsMock' };
        if (status === 403 || status === 423 || status === 429) return { kind: 'gate', error: e };
        if (status === 404) break; // по этой ступени давать нечего — следующая
      }
      if (tries >= RETRY_DELAYS.length) return { kind: 'failed' };
      await sleep(RETRY_DELAYS[tries]);
    }
  }
  return { kind: 'creating' };
}
